<template>
  <div>
    <b-modal
      id="export-templates-modal"
      ref="export-templates-modal"
      title="Export"
      title-class="text-lg font-semibold leading-none tracking-tight"
      centered
    >
      <b-card no-body>
        <b-tabs
          pills
          card
        >
          <b-tab
            title="Download"
            class="export-download-tab"
            active
          >
            <div 
              v-if="!['idml'].includes(selectedFormat)"
              class="form-row export-field-row export-order-saved-settings"
            >
              <div class="col-md-2">
                <label>Saved settings</label>
              </div>

              <div class="col-md-10 form-group">
                <ExportSelect
                  v-model="selectedDownloadSettings"
                  :options="downloadSettingsOptions"
                  placeholder="Select settings"
                  :disabled="useTemplateSettings"
                  emit-option
                  @select="applySavedSettings"
                />
              </div>
            </div>

            <!-- Only show the Format field if not using template settings
                 or if using template settings and all selected templates have identical settings -->
            <div
              v-if="!useTemplateSettings || templateSettingsConsistent"
              class="form-row export-field-row export-order-format"
            >
              <div class="col-md-2">
                <label>Format</label>
              </div>
              <div class="col-md-10 form-group">
                <ExportSelect
                  v-model="selectedFormat"
                  :options="formats"
                  class="form-control"
                  :disabled="useTemplateSettings"
                  @change="onExportTypeUpdated"
                />
                <div
                  v-if="warningMessage"
                  class="d-flex align-items-center alert-warning mt-2 font-xs px-2 py-1"
                  style="gap: 8px; width: fit-content; height: fit-content;"
                >
                  <i class="bi bi-exclamation-triangle" />
                  <span>
                    {{ warningMessage }}
                  </span>
                </div>
              </div>
            </div>

            <div v-show="selectedFormat === 'pdf'">
              <div class="form-row export-field-row export-format-option">
                <div class="col-md-2">
                  <label>Size</label>
                </div>
                <div class="col-md-10 form-group">
                  <ExportSelect
                    v-model="selectedPdfPageSize"
                    :options="pdfPageSizes"
                    class="form-control"
                  />
                </div>
              </div>
            </div>
            <div v-show="selectedFormat === 'pptx'">
              <div class="form-row export-field-row export-format-option">
                <div class="col-md-2">
                  <label>Size</label>
                </div>
                <div class="col-md-10 form-group">
                  <ExportSelect
                    v-model="selectedPptxPageSize"
                    :options="pptxPageSizes"
                    class="form-control"
                  />
                </div>
              </div>
            </div>
            <div v-show="selectedFormat === 'pptx'">
              <div class="form-row export-field-row export-format-option">
                <div class="col-md-2">
                  <label>PPTX output type</label>
                </div>
                <div class="col-md-10 form-group">
                  <ExportSelect
                    v-model="pptxIncludeFonts"
                    :options="pptxOutputTypes"
                    class="form-control"
                    @change="setSavedSettingsToNull"
                  />
                </div>
              </div>
            </div>
            <div
              v-show="selectedFormat === 'gif' || selectedFormat === 'pdf'"
              class="export-order-download"
            >
              <div class="form-row export-field-row container-downloadable-groups">
                <div class="col-md-2">
                  <label>Group</label>
                </div>
                <div class="col-md-10 form-group">
                  <MultipleSelectJquery
                    v-model="selectedGroupInstances"
                    class="export-pdf-groups-dropdown"
                    multiple
                    :data="groupsInstances"
                    :options="downloadMultipleSelectOptions"
                    placeholder="Select groups..."
                  />
                </div>
              </div>

              <div
                v-if="selectedFormat === 'pdf' && selectedGroupInstances.length == 1"
                class="form-row export-field-row export-format-option"
              >
                <div class="col-md-2" />
                <div class="col-md-10 form-group export-checkbox-setting">
                  <label>
                    <input
                      v-model="getDirectLinktoFile"
                      type="checkbox"
                      @change="setSavedSettingsToNull"
                    >
                    Get direct link to the file
                  </label>
                </div>
              </div>

              <div 
                v-if="selectedFormat !== 'pdf'"
                class="form-row export-field-row container-downloadable-single-template"
              >
                <div class="col-md-2">
                  <label>Single Templates</label>
                </div>
                <div class="col-md-10 form-group">
                  <MultipleSelectJquery
                    v-model="selectedSingleInstances"
                    multiple
                    :data="singleInstances"
                    :options="downloadMultipleSelectOptions"
                    placeholder="Select templates..."
                    @change="setSavedSettingsToNull"
                  />
                </div>
              </div>
            </div>

            <div
              v-show="!['gif', 'pdf'].includes(selectedFormat)"
              class="export-order-download"
            >
              <div class="form-row export-field-row container-downloadable-templates">
                <div class="col-md-2">
                  <label>Download</label>
                </div>
                <div class="col-md-10 form-group">
                  <Multiselect
                    v-model="selectedInstances"
                    :options="selectableInstances"
                    track-by="instance_id"
                    label="name"
                    :loading="isTemplatesLoading"
                    :disabled="isTemplatesLoading"
                    placeholder="Select instances..."
                    :close-on-select="false"
                    :show-labels="false"
                    multiple
                    @select="setSavedSettingsToNull"
                    @remove="setSavedSettingsToNull"
                    @open="isDownloadDropdownOpen = true"
                    @close="isDownloadDropdownOpen = false"
                  >
                    <template #selection="{ isOpen }">
                      <span
                        v-if="selectedTemplateCount && !isOpen"
                        class="export-selection-summary"
                      >
                        {{ selectedTemplateCount }} {{ selectedTemplateCount === 1 ? 'template' : 'templates' }} selected
                      </span>
                    </template>
                    <template #beforeList>
                      <div class="export-template-actions">
                        <button
                          v-if="!allInstancesSelected"
                          type="button"
                          @mousedown.prevent
                          @click.stop="selectAllInstances"
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          class="export-template-actions__unselect"
                          @mousedown.prevent
                          @click.stop="unselectAllInstances"
                        >
                          Unselect all
                        </button>
                      </div>
                    </template>
                    <template #option="{ option }">
                      <div class="export-template-option">
                        <span
                          class="export-template-option__checkbox"
                          :class="{ 'export-template-option__checkbox--checked': isInstanceSelected(option) }"
                          aria-hidden="true"
                        />
                        <span :title="option.name">{{ option.name }}</span>
                      </div>
                    </template>
                  </Multiselect>
                </div>
              </div>
            </div>

            <div
              v-if="['pptx'].includes(selectedFormat)"
              class="form-row export-field-row export-format-option"
            >
              <div class="col-md-2">
                <label>Naming</label>
              </div>
              <div class="col-md-10 form-group">
                <ExportSelect
                  v-model="selectedNaming"
                  :options="NAMINGS"
                  class="form-control"
                />
              </div>
            </div>
            
            <div
              v-show="['pptx'].includes(selectedFormat)"
              class="form-row export-field-row export-format-option"
            >
              <div class="col-md-2" />
              <div class="col-md-10 export-checkbox-setting">
                <label>
                  <input
                    v-model="downloadAsOneFile"
                    type="checkbox"
                    @change="setSavedSettingsToNull"
                  >
                  Download as one file
                </label>
              </div>
            </div>

            <div
              v-if="['jpg', 'png', 'tiff', 'psd', 'jpg_from_psd', 'png_from_psd', 'psd_and_jpg'].includes(selectedFormat)"
              class="form-row export-field-row export-format-option"
            >
              <div class="col-md-2" />
              <div class="col-md-10 form-group">
                <label class="export-synthetic-performer-option">
                  <input
                    v-model="containsSyntheticPerformer"
                    type="checkbox"
                  >
                  <span class="export-synthetic-performer-option__content">
                    <span class="export-checkbox-setting__label">Include “contains-synthetic-performer” tag</span>
                    <span class="export-synthetic-performer-option__description">(use if photorealistic person was generated by AI)</span>
                  </span>
                </label>
              </div>
            </div>

            <div
              v-show="['psd', 'jpg_from_psd', 'psd_and_jpg', 'pptx', /*'pdf', */'png_from_psd'].includes(selectedFormat)"
              class="form-row export-field-row export-format-option export-format-option--checks"
            >
              <div class="col-md-2" />
              <div class="col-md-10 form-group export-inline-checks">
                <label>
                  <input
                    v-model="downloadInBackground"
                    type="checkbox"
                    @change="setSavedSettingsToNull"
                  >
                  {{ selectedFormat === 'jpg_from_psd' ? 'Email me when export is ready' : 'Download in background' }}
                </label>
                <label
                  v-show="selectedFormat === 'jpg_from_psd'"
                  title="Compares exported images with on-screen images"
                >
                  <input
                    v-model="exportValidation"
                    type="checkbox"
                    @click="exportValidation = !exportValidation"
                  >
                  Export validation
                </label>
              </div>
            </div>

            <div
              v-show="selectedFormat === 'jpg_from_psd' && downloadInBackground"
              class="form-row export-field-row export-format-option"
            >
              <div class="col-md-2" />
              <div class="col-md-10 form-group">
                <input
                  v-model="notifyEmail"
                  type="email"
                  class="form-control"
                  placeholder="Email address"
                  @change="setSavedSettingsToNull"
                >
              </div>
            </div>

            <div
              v-if="['pdf'].includes(selectedFormat)"
              class="form-row export-field-row export-format-option"
            >
              <div class="col-md-2">
                <label>Settings</label>
              </div>
              <div class="col-md-10">
                <Multiselect
                  v-model="selectedPdfOptions"
                  class="export-settings-dropdown"
                  :options="pdfOptions"
                  placeholder="Select options"
                  track-by="value"
                  label="text"
                  :multiple="true"
                  @input="applyPdfOptions"
                />
              </div>
            </div>

            <!-- DPI and related settings are only shown if not using template settings
                 or if using template settings and all selected templates share the same settings -->
            <div
              v-if="(!useTemplateSettings || templateSettingsConsistent) && ['jpg', 'png', 'pdf', 'jpg_from_psd', 'png_from_psd'].includes(selectedFormat)"
              class="export-field-row export-output-row export-order-output"
            >
              <label class="export-field-label">Output</label>
              <div
                class="export-output-grid"
                :class="{ 'export-output-grid--single': !['jpg', 'png'].includes(selectedFormat) }"
              >
                <div class="export-output-field">
                  <label>DPI</label>
                  <div class="export-dpi-controls">
                    <ExportSelect
                      v-model.number="selectedDpi"
                      :options="dpiPresets"
                      :disabled="useTemplateSettings"
                      @change="updateDpi"
                    />
                    <b-form-input
                      v-if="!selectedDpi"
                      v-model.number="dpi"
                      type="number"
                      autofocus
                      :disabled="useTemplateSettings"
                      @change="setSavedSettingsToNull"
                    />
                  </div>
                </div>

                <div
                  v-if="['jpg', 'png'].includes(selectedFormat)"
                  class="export-output-field"
                >
                  <label>Size</label>
                  <ExportSelect
                    v-model="selectedSize"
                    :options="sizeSelectOptions"
                    class="form-control"
                    :disabled="useTemplateSettings"
                    @change="setSavedSettingsToNull"
                  />
                  <div v-if="selectedSize === SIZE_OPTIONS_TYPE.SCALE_DOWN_BY_PERCENTAGE">
                    <div class="my-2" />
                    <b-form-input
                      v-model="size.percentage"
                      type="number"
                      @change="setSavedSettingsToNull"
                    />
                  </div>
                  <div
                    v-if="selectedSize === SIZE_OPTIONS_TYPE.SCALE_DOWN_BY_DIMENSIONS"
                    class="dimensions-container"
                  >
                    <div class="my-2" />
                    <b-form-input
                      v-model="size.dimensions.width"
                      type="number"
                      @change="handleWidthChange"
                    />
                    x
                    <b-form-input
                      v-model="size.dimensions.height"
                      type="number"
                      @change="handleHeightChange"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div 
              v-show="selectedFormat === 'pdf' && useMpdf == true"
              class="form-row export-field-row export-format-option"
            >
              <div class="col-md-2">
                <label>Color profile</label>
              </div>   
              <div class="col-md-10 form-group">
                <ExportSelect
                  v-model="selectedColorProfile"
                  :options="colorProfilePresets"
                  @change="updateColorProfile"
                />
              </div>
            </div>
            <div
              v-show="selectedFormat === 'pdf'"
              class="form-row export-field-row export-format-option export-bleed-option"
            >
              <div class="col-md-2 mt-2">
                <label>Bleed</label>
                <label
                  v-b-tooltip.hover
                  class="bleed-link-toggle"
                  :title="bleedLinked ? 'Unlink sides' : 'Link all sides'"
                >
                  <input
                    v-model="bleedLinked"
                    type="checkbox"
                    @change="onBleedLinkToggle"
                  >
                  <i
                    class="fas"
                    :class="bleedLinked ? 'fa-link' : 'fa-unlink'"
                  />
                  <span>Link sides</span>
                </label>
              </div>
              <div class="col-md-10 mt-2">
                <div class="form-row export-bleed-grid">
                  <div class="col-md-2 form-group">
                    <label class="bleed-label">Top</label>
                    <b-form-input
                      v-model.number="bleed.top"
                      type="number"
                      min="0"
                      step="any"
                      @input="handleBleedInput('top')"
                    />
                  </div>
                  <div class="col-md-2 form-group">
                    <label class="bleed-label">Bottom</label>
                    <b-form-input
                      v-model.number="bleed.bottom"
                      type="number"
                      min="0"
                      step="any"
                      @input="handleBleedInput('bottom')"
                    />
                  </div>
                  <div class="col-md-2 form-group">
                    <label class="bleed-label">Left</label>
                    <b-form-input
                      v-model.number="bleed.left"
                      type="number"
                      min="0"
                      step="any"
                      @input="handleBleedInput('left')"
                    />
                  </div>
                  <div class="col-md-2 form-group">
                    <label class="bleed-label">Right</label>
                    <b-form-input
                      v-model.number="bleed.right"
                      type="number"
                      min="0"
                      step="any"
                      @input="handleBleedInput('right')"
                    />
                  </div>
                  <div class="col-md-4 form-group">
                    <label class="bleed-label">Unit</label>
                    <ExportSelect
                      v-model="bleed.unit"
                      :options="bleedUnits"
                      @change="setSavedSettingsToNull"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div
              v-show="selectedFormat === 'pdf'"
              class="form-row export-field-row export-format-option"
            >
              <div class="col-md-2" />
              <div class="col-md-10 form-group export-checkbox-setting">
                <label>
                  <input
                    v-model="trimMarks"
                    type="checkbox"
                    @change="setSavedSettingsToNull"
                  >
                  Trim marks
                </label>
              </div>
            </div>
            <div
              v-if="isXlsxPriceFormatSelected"
              class="form-row export-field-row export-format-option container-platforms"
            >
              <div class="col-md-2">
                <label>Export Type</label>
              </div>
              <div class="col-md-10 form-group">
                <ExportSelect
                  v-model="xlsxPrice.selectedExportType"
                  class="form-control"
                  :options="xlsxPrice.exportTypeOptions"
                />
              </div>
            </div>
            <div
              v-if="isXlsxPriceFormatSelected && ['CreateNewTabForEachTemplate', 'CreateXlsxForEachTemplate'].includes(xlsxPrice.selectedExportType)"
              class="form-row export-field-row export-format-option container-platforms"
            >
              <div class="col-md-2">
                <label>Tab Names Source</label>
              </div>
              <div class="col-md-10 form-group">
                <ExportSelect
                  v-model="xlsxPrice.selectedTabNameSource"
                  class="form-control"
                  :options="xlsxPrice.tabNameSourceOptions"
                />
              </div>
            </div>
            <div
              v-if="selectedFormat === 'html'"
              class="form-row export-field-row export-format-option container-platforms"
            >
              <div class="col-md-2">
                <label>Platform</label>
              </div>
              <div class="col-md-10 form-group">
                <ExportSelect
                  v-model="selectedPlatform"
                  :options="platformOptions"
                  class="form-control"
                  @change="setSavedSettingsToNull"
                />
              </div>
            </div>

            <div v-if="checkTestMode()">
              <div class="form-row export-field-row export-format-option export-test-mode-option">
                <div class="col-md-2">
                  <label>Test mode
                    <i
                      class="cursor-pointer glyphicon glyphicon-refresh reset-text-position"
                      title="Get data to json"
                      @click="setJsonText"
                    />
                  </label>
                </div>
                <div class="col-md-10 form-group">
                  <textarea
                    v-model="jsonText"
                    rows="15"
                    cols="80"
                    class="json-editor"
                    @input="validateJSON"
                  />
                  <p
                    v-if="errorMessage"
                    class="error-message"
                  >
                    {{ errorMessage }}
                  </p>
                </div>
              </div>
            </div>

            <div
              v-if="(!useTemplateSettings || templateSettingsConsistent) && compressionCanBeApplied"
              class="form-row export-field-row export-order-compression"
            >
              <div />
              <div class="export-checkbox-setting">
                <label for="compressToMaxFileSize">
                  <input
                    id="compressToMaxFileSize"
                    v-model="compressToMaxFileSize"
                    type="checkbox"
                    :disabled="useTemplateSettings"
                    @change="handleCheckboxChange('compressToMaxFileSize')"
                  >
                  <span>Max file size</span>
                </label>
                <div
                  v-if="compressToMaxFileSize"
                  class="export-max-file-size"
                >
                  <b-form-input
                    v-model="compressToMaxFileSizeValue"
                    type="number"
                    placeholder="Max file size"
                    :disabled="useTemplateSettings"
                    @change="setSavedSettingsToNull"
                  />
                  <span>KB</span>
                </div>
              </div>
            </div>

            <div
              v-if="!['feed','idml', 'xlsx_price'].includes(selectedFormat)"
              class="form-row export-field-row export-order-folder -groups"
            >
              <div class="col-md-2">
                <label>Save to folder</label>
              </div>
              <div class="col-md-10 form-group">
                <FolderTreeSelect
                  ref="selectedFolderInput"
                  v-model="selectedFolder"
                  :items="folders"
                  :new-folder-name="isNewFolder ? newFolderName : ''"
                  placeholder="Browse File Browser folders..."
                  @input="handleFolderSelection"
                  @create-folder="handleNewFolderSelection"
                  @clear-new-folder="clearNewFolderSelection"
                />
                <div v-show="selectedFolder && selectedFormat">
                  <div>
                    <button
                      class="btn btn-primary my-2 font-xs"
                      @click="openFolderInNewTab"
                    >
                      Open folder
                    </button>
                    <button
                      class="btn btn-primary font-xs"
                      @click="copyToClipboard"
                    >
                      Copy link
                    </button>
                    <button
                      class="btn btn-primary font-xs"
                      :disabled="isGeneratingShareLink"
                      @click="copySharedToClipboard"
                    >
                      <span
                        v-if="isGeneratingShareLink"
                        class="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                      Copy share link
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="hasAdvancedOptions"
              class="export-advanced export-order-advanced"
            >
              <button
                v-b-toggle.export-advanced-options
                type="button"
                class="export-advanced-toggle"
              >
                <span>Advanced</span>
                <i class="bi bi-chevron-down" />
              </button>

              <b-collapse id="export-advanced-options">
                <div class="export-advanced-content">
                  <div
                    v-if="templateSettingsCanBeApplied"
                    class="form-row export-field-row"
                  >
                    <div />
                    <div class="export-checkbox-setting">
                      <label for="useTemplateSettings">
                        <input
                          id="useTemplateSettings"
                          v-model="useTemplateSettings"
                          type="checkbox"
                          @change="handleCheckboxChange('useTemplateSettings')"
                        >
                        <span class="template-settings-label">Use template settings</span>
                      </label>
                    </div>
                  </div>

                  <div
                    v-if="bulkNamingCanBeApplied"
                    class="form-row export-field-row export-advanced-bulk-naming"
                  >
                    <div>
                      <label for="bulkNaming">
                        <input
                          id="bulkNaming"
                          v-model="isBulkNaming"
                          type="checkbox"
                          @change="setBulkNaming"
                        >
                        <span>Bulk naming</span>
                      </label>
                    </div>
                    <div>
                      <input
                        v-model="bulkName"
                        type="text"
                        class="form-control"
                        placeholder="Enter base name (e.g. Product_Hero)"
                        :disabled="!isBulkNaming"
                        @input="applyBulkName"
                      >
                      <div
                        v-if="isBulkNaming"
                        class="export-bulk-names-editor"
                      >
                        <div class="export-bulk-names-editor__header">
                          <span>Filenames (one per line)</span>
                          <button
                            type="button"
                            @click="resetBulkNames"
                          >
                            Reset all
                          </button>
                        </div>
                        <textarea
                          class="form-control"
                          :value="bulkNamesText"
                          rows="5"
                          @input="onBulkNamesInput"
                        />
                        <div class="export-bulk-names-editor__footer">
                          {{ selectedTemplateCount }} {{ selectedTemplateCount === 1 ? 'line' : 'lines' }} expected
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </b-collapse>
            </div>

            <div 
              v-if="errors.length" 
              class="form-row export-field-row export-order-errors"
            >
              <div class="col-md-2" />
              <div class="col-md-10">
                <div
                  v-for="(error, index) in errors"
                  :key="index"
                  class="alert alert-warning"
                  role="alert"
                >
                  {{ error }}
                </div>
              </div>
            </div>
          </b-tab>

          <b-tab title="Saved settings">
            <SavedDownloadSettingsTab
              ref="savedDownloadSettingsTab"
              :is-master-admin="isMasterAdmin"
              @settings-updated="onSettingsUpdated"
            />
          </b-tab>
          <b-tab
            v-if="compressionCanBeApplied"
            title="File Sizes"
          >
            <ExportFileSizesTab
              v-model="perTemplateMaxFileSizes"
              :instances="instances"
              :selected-instances="selectedInstances"
              :disabled="fileSizesTabDisabled"
              @input="setSavedSettingsToNull"
            />
          </b-tab>
          <b-tab
            v-if="['pptx', 'pdf'].includes(selectedFormat)"
            title="Page numbers"
          >
            <ExportPageNumerationTab
              :page-numeration="pageNumeration"
              @data-updated="onPageNumerationUpdate($event)"
            />
          </b-tab>
          <b-tab
            v-if="['pptx', 'pdf'].includes(selectedFormat)"
            title="Page header"
          >
            <ExportPageEdgeTab
              label-text="Header Height"
              :section-data="pageHeader"
              @data-updated="pageHeader = $event"
            />
          </b-tab>
          <b-tab
            v-if="['pptx', 'pdf'].includes(selectedFormat)"
            title="Page footer"
          >
            <ExportPageEdgeTab
              label-text="Footer Height"
              :section-data="pageFooter"
              @data-updated="pageFooter = $event"
            />
          </b-tab>

          <b-tab
            v-if="['pdf'].includes(selectedFormat)"
            title="Add pages"
          >
            <ExportAddPagesTab
              :add-pages="addPages"
              :group-instances="groupsInstances"
              @data-updated="addPages = $event"
            />
          </b-tab>
          <b-tab
            v-if="['pdf'].includes(selectedFormat)"
            title="Links"
          >
            <ExportLinksTab
              :links="links"
              @data-updated="links = $event"
            />
          </b-tab>
        </b-tabs>
      </b-card>

      <template #modal-footer>
        <div
          v-if="showBar && isSaving"
          class="col-12"
        >
          <ProgressBarComponent
            :current="current"
            :total="total"
          />
        </div>
        <div class="d-flex w-100 justify-content-end">
          <a
            v-if="!['idml'].includes(selectedFormat)"
            href=""
            class="export-save-settings-link m-1"
            @click="showSaveSettingsModal"
          >
            Save settings
          </a>
          <a
            v-if="!['idml'].includes(selectedFormat) && activeDownloadSetting !== null"
            href=""
            class="export-save-settings-link m-1"
            @click="showUpdateSettingsModal"
          >
            Update settings
          </a>
          <b-button
            v-if="lastResults.saveToFolder.length > 0 && (selectedFormat === 'pptx' || selectedFormat === 'pdf')"
            :disabled="isDownloadingFileList"
            variant="primary"
            class="m-1 ml-3"
            @click="downloadFileList"
          >
            <div v-if="isDownloadingFileList">
              <span
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
              Downloading File List...
            </div>
            <span v-else>Download File List</span>
          </b-button>
          <b-button
            class="m-1 ml-auto"
            variant="primary"
            :disabled="isSaving"
            @click="onSave"
          >
            <div v-if="isSaving">
              <span
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
              Exporting...
            </div>
            <span v-else>{{ selectedFormat === 'jpg_from_psd' && downloadInBackground ? 'Queue export' : 'Export' }}</span>
          </b-button>
          <b-button
            class="m-1"
            variant="secondary"
            @click="onClose"
          >
            Cancel
          </b-button>
        </div>
      </template>
    </b-modal>

    <SaveDownloadSettingsModal
      ref="saveDownloadSettingsModal"
      :is-master-admin="isMasterAdmin"
      @saved="onSettingSaved"
    />
  </div>
</template>

<script>
/* eslint vue/enforce-style-attribute: 0 */
/* global previews */
import { exportTemplatesEventBroker, EVENTS } from '@frontend/group/modules/export-templates/event-broker';
import MultipleSelectJquery from '../../../common/atoms/MultipleSelectJquery.vue';
import Multiselect from "vue-multiselect";
import 'vue-multiselect/dist/vue-multiselect.min.css';
import {SIZE_OPTIONS_TYPE} from "@frontend/constants/layout-export";
import layoutApi from "@frontend/services/api/layout";
import folderService from '@frontend/services/api/folder';
import FolderTreeSelect from "@frontend/components/common/atoms/FolderTreeSelect/FolderTreeSelect.vue";
import Pusher from "pusher-js";
import ProgressBarComponent from "@frontend/components/ProgressBarComponent.vue";
import SaveDownloadSettingsModal from "@frontend/components/modules/layouts-page/modals/SaveDownloadSettingsModal.vue";
import SavedDownloadSettingsTab from "@frontend/components/modules/layouts-page/Export/SavedDownloadSettingsTab.vue";
import ExportPageNumerationTab from "@frontend/components/modules/layouts-page/Export/ExportPageNumerationTab.vue";
import ExportPageEdgeTab from "@frontend/components/modules/layouts-page/Export/ExportPageEdgeTab.vue";
import {applySavedSettings, getSavedSettings} from "@frontend/group/modules/export-templates/saved-settings-service";
import ExportAddPagesTab from "@frontend/components/modules/layouts-page/Export/ExportAddPagesTab.vue";
import ExportLinksTab from "@frontend/components/modules/layouts-page/Export/ExportLinksTab.vue";
import {
  ADDITIONAL_MULTI_LINE_TEXT_FIELD_PREFIX_ELEMENT_ID
} from "@frontend/constants/templates-field-prefixes";
import FileService from "@frontend/services/api/file-browser";
import {downloadFileLink} from "@frontend/components/modules/file-browser/helpers";
import {rulerEventsBroker, EVENTS as RULER_EVENTS} from "@frontend/group/modules/ruler/event-broker";
import {FILE_TYPE} from "@frontend/constants/file-image-types";
import {mapGetters} from "vuex";
import {
  GENERATE_QUICK_DOWNLOAD_EVENTS,
  generateQuickDownloadEventBroker
} from "@frontend/group/modules/generate-quick-download/event-broker/event-broker";
import ExportFileSizesTab from "./components/ExportFileSizesTab.vue";
import ExportSelect from "./components/ExportSelect.vue";

const initSize = {
  percentage: 100,
  dimensions: {
    width: null,
    height: null,
  }
};

const COLOR_PROFILE_PRESETS = [
  {
    text: 'RGB',
    value: 'RGB'
  },  
  {
    text: 'CMYK - ISO Coated v2 (FOGRA39)',
    value: 'CMYK-FOGRA39'
  },
  {
    text: 'CMYK - PSO Coated v3 (FOGRA51)',
    value: 'CMYK-FOGRA51'
  },
  {
    text: 'CMYK - PSO Uncoated (FOGRA52)',
    value: 'CMYK-FOGRA52'
  }
];

const DPI_PRESETS = [
  {
    text: '72',
    value: 72
  },
  {
    text: '75',
    value: 75
  },
  {
    text: '100',
    value: 100
  },
  {
    text: '144',
    value: 144
  },
  {
    text: '150',
    value: 150
  },
  {
    text: '200',
    value: 200
  },
  {
    text: '300',
    value: 300
  },
  {
    text: 'Custom',
    value: 0
  },
]

const NAMING = {
  STANDARD: 'Standard',
  PRODUCT_ID: 'Product_ID',
  PROJECT_NAME: 'Project Name',
};


export default {
  name: 'ExportTemplatesModal',
  components: {
    ExportPageEdgeTab,
    FolderTreeSelect,
    MultipleSelectJquery,
    Multiselect,
    ProgressBarComponent,
    SaveDownloadSettingsModal,
    SavedDownloadSettingsTab,
    ExportPageNumerationTab,
    ExportAddPagesTab,
    ExportLinksTab,
    ExportFileSizesTab,
    ExportSelect
  },
  data() {
    return {
      isSaving: false,
      selectedPdfPageSize: 'none',
      dpiPresets: DPI_PRESETS,
      colorProfilePresets: COLOR_PROFILE_PRESETS,
      pdfPageSizes: [
        {
          text: 'Use Template Size (Default)',
          value: 'none'
        },
        {
          text: 'Letter (Portrait) - 2550x3300',
          value: 'letter-portrait'
        },
        {
          text: 'Letter (Landscape) - 3300x2550',
          value: 'letter-landscape'
        },
        {
          text: 'A4 (Portrait) - 2480x3508',
          value: 'a4-portrait'
        },
        {
          text: 'A4 (Landscape) - 3508x2480',
          value: 'a4-landscape'
        },
      ],
      selectedPptxPageSize: '',
      pptxPageSizes: [
        {
          text: 'Use Template Size (Default)',
          value: ''
        },
        {
          text: 'PowerPoint (Widescreen 16:9) - 4000x2550',
          value: 'screen16x9'
        },
        {
          text: 'PowerPoint (Standard 4:3) - 3000x1668',
          value: 'screen4x3'
        },
        {
          text: 'Letter (Portrait) - 2550x3300',
          value: 'letterPortrait'
        },
        {
          text: 'Letter (Landscape) - 3300x2550',
          value: 'letter'
        },
        {
          text: 'A4 (Portrait) - 2480x3508',
          value: 'A4Portrait'
        },
        {
          text: 'A4 (Landscape) - 3508x2480',
          value: 'A4'
        },
      ],
      formats: [],
      dpi: 72,
      selectedDpi: 72,
      selectedColorProfile: 'RGB',
      selectedFormat: 'jpg',
      selectedNaming: NAMING.STANDARD,
      selectedPlatform: '',
      selectedSize: SIZE_OPTIONS_TYPE.KEEP_ORIGINAL,
      size: {...initSize},
      templateDimensions: {
        width: null,
        height: null,
      },
      isTemplatesLoading: false,
      isDownloadDropdownOpen: false,
      templateAspectRatio: 1,
      selectedInstances: [],
      selectedGroupInstances: [],
      selectedSingleInstances: [],
      downloadMultipleSelectOptions: {
        maxHeight: 280,
      },
      selectedFolder: null,
      instances: [],
      groupsInstances: [],
      singleInstances: [],
      folders: null,
      selectAllText : 'Select All',
      createPageForEachTemplate: false,
      createOneFilePerGroup: false,
      groupLayersNameByProjectName: false,
      getDirectLinktoFile: false,
      downloadInBackground: false,
      notifyEmail: window.userEmail || '',
      downloadAsOneFile: true,
      singleInstanceId: null,
      groupInstancesMap: [],
      pptxOutputTypes: [
        {
          text: 'PPTX only',
          value: false
        },
        {
          text: 'PPTX and fonts in zip file',
          value: true
        }
      ],
      pptxIncludeFonts: false,
      layoutId: null,
      groupsTemplates: null,
      layoutOptions: null,
      errorMessage: null,
      warningMessage: null,
      jsonText: `{}`,
      showBar: false,
      total: 0,
      current: 0,
      selectedDownloadSettings: null,
      activeDownloadSetting: null,
      selectedPdfOptions: [],
      pdfOptions: [
        {text: 'Add background to images', value: 'addBackgroundToImagesPdf'},
        {text: 'Create one file for each group', value: 'createOneFilePerGroup'},
        {text: 'Create one page for each template in the group', value: 'createPageForEachTemplate'},
        {text: 'Group layout templates by Project Names', value: 'groupLayersNameByProjectName'},
        {text: 'Download in background', value: 'downloadInBackground'},
        {text: 'Use mPDF', value: 'useMpdf'},
        {text: 'Native tables', value: 'nativePdfTables'},
        {text: 'Show printable margins', value: 'displayPrintableArea'},
        {text: 'Transparent table image background', value: 'tableImageTransparentBackground'},
      ],
      addBackgroundToImagesPdf: false,
      tableImageTransparentBackground: false,
      bleed: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        unit: 'px',
      },
      bleedUnits: [
        {text: 'Pixels', value: 'px'},
        {text: 'Inches', value: 'in'},
      ],
      bleedLinked: true,
      trimMarks: false,
      downloadSettingsOptions: [],
      isMasterAdmin: false,
      compressToMaxFileSize: false,
      compressToMaxFileSizeValue: null,
      pageNumeration: {
        isEnabled: false,
        mirrorType: 'None',
        horizontalOffset: 0,
        verticalOffset: 0,
        fontFamily: 'Arial',
        fontSize: 50,
        fontColor: '#000000',
        horizontalPosition: 'Right',
        verticalPosition: 'Bottom',
        underlyingShape: {
          isEnabled: false,
          shapeType: 'Image',
          image: {
            url: '',
            width: 0,
            height: 0,
            horizontalPosition: 'Left',
            verticalPosition: 'Top',
            horizontalOffset: 0,
            verticalOffset: 0,
          }
        },
        resultData:{}
      },
      pageFooter: {
        isEnabled: false,
        height: 50,
        backgroundColor: '#858585',
        texts: [],
        icon: {
          isEnabled: false,
          height: 40,
          width: 40,
          horizontalPosition: 'Left',
          verticalPosition: 'Center',
          horizontalOffset: 0,
          verticalOffset: 0,
        },
      },
      pageHeader: {
        isEnabled: false,
        height: 50,
        backgroundColor: '#858585',
        texts: [],
        icon: {
          isEnabled: false,
          height: 40,
          width: 40,
          horizontalPosition: 'Left',
          verticalPosition: 'Center',
          horizontalOffset: 0,
          verticalOffset: 0,
        },
      },
      addPages: {
        prepend: {
          isEnabled: false,
        },
        append: {
          isEnabled: false,
        },
        insert: {
          isEnabled: false,
          insertions: [],
        },
      },
      links: [],
      useTemplateSettings: false,
      page: null,
      limit: null,
      tocLastPage: 0,
      templateSettings: {
        DPI: this.dpi,
        'File Type': this.selectedFormat,
        'Max File Size': null,
      },
      uniqueTemplateSettings:{},
      selectedTemplateSettings: null,
      usePSD3: true,
      useMpdf: true,
      nativePdfTables: false,
      displayPrintableArea: false,
      errors: [],
      lastResults: {
        saveToFolder: []
      },
      isDownloadingFileList: false,
      xlsxPrice: {
        exportTypeOptions: [
          {text: 'Group all Templates in one Tab', value: 'GroupAllTemplatesInOneTab'},
          {text: 'Create a new Tab for each Template', value: 'CreateNewTabForEachTemplate'},
          {text: 'Create XLSX for each Template', value: 'CreateXlsxForEachTemplate'},
        ],
        selectedExportType: "GroupAllTemplatesInOneTab",
        tabNameSourceOptions: [
          {text: 'Use Template Names as Tab Names', value: 'TemplateNames'},
          {text: 'Project Names as Tab Names', value: 'ProjectNames'},
          {text: 'Product IDs as Tab Names', value: 'ProductId'},
        ],
        selectedTabNameSource: "TemplateNames",
      },
      reloadTemplates: false,
      isNewFolder: false,
      newFolderName: null,
      isGeneratingShareLink: false,
      perTemplateMaxFileSizes: {},
      isBulkNaming: false,
      bulkName: 'filename',
      bulkNames: [],
      isApplyingSavedSettings: false,
      containsSyntheticPerformer: false,
      exportValidation: true,
    };
  },
  computed:{
    ...mapGetters('layout', [
      'selectedInstance',
    ]),
    NAMINGS: () => Object.values(NAMING),
    SIZE_OPTIONS_TYPE() {
      return SIZE_OPTIONS_TYPE
    },
    sizeSelectOptions() {
      const options = [
        {text: 'Keep original size', value: SIZE_OPTIONS_TYPE.KEEP_ORIGINAL},
        {text: 'Scale down by %', value: SIZE_OPTIONS_TYPE.SCALE_DOWN_BY_PERCENTAGE},
      ];

      if (this.selectedInstances.length === 1) {
        options.push({
          text: 'Custom dimensions',
          value: SIZE_OPTIONS_TYPE.SCALE_DOWN_BY_DIMENSIONS,
        });
      }

      return options;
    },
    platformOptions() {
      return [
        {text: 'None', value: ''},
        {text: 'The Trade Desk (TTD)', value: 'ttd'},
      ];
    },
    instancesOption() {
      const selectAllOption = { instance_id: -1, name: this.selectAllText };
      return [selectAllOption, ...this.instances];
    },
    selectableInstances() {
      return this.instancesOption.filter(option => option.instance_id !== -1);
    },
    selectedTemplateCount() {
      return this.selectedInstances.filter(option => option.instance_id !== -1).length;
    },
    allInstancesSelected() {
      return this.instances.length > 0 && this.instances.every(instance => this.isInstanceSelected(instance));
    },
    compressionCanBeApplied() {
      return ['jpg', 'jpg_from_psd', 'png_from_psd', 'png'].includes(this.selectedFormat);
    },
    templateSettingsCanBeApplied() {
      return ['jpg', 'jpg_from_psd', 'png', 'psd', 'png_from_psd'].includes(this.selectedFormat);
    },
    bulkNamingCanBeApplied() {
      return !['gif', 'pdf', 'pptx', 'feed', 'xlsx_price', 'html'].includes(this.selectedFormat);
    },
    hasAdvancedOptions() {
      return this.templateSettingsCanBeApplied || this.bulkNamingCanBeApplied;
    },
    pdfPageSizeConfig() {
      const DPI = 300;
      
      switch (this.selectedPdfPageSize) {
        case 'letter-portrait':
          return {
            dpi: DPI,
            type: 'letter',
            orientation: 'portrait',
            width: 2550,
            height: 3300
          }
        case 'letter-landscape':
          return {
            dpi: DPI,
            type: 'letter',
            orientation: 'landscape',
            width: 3300,
            height: 2550
          }
        case 'a4-portrait':
          return {
            dpi: DPI,
            type: 'a4',
            orientation: 'portrait',
            width: 2480,
            height: 3508,
          }
        case 'a4-landscape':
          return {
            dpi: DPI,
            type: 'a4',
            orientation: 'landscape',
            width: 3508,
            height: 2480,
          }
        default:
          return null;
      }
    },
    disableUnrenderedTemaplates() {
      return this.selectedFormat === 'pdf' && !this.downloadInBackground;
    },
    templateSettingsConsistent() {
      if (!this.useTemplateSettings) return true;
      if (this.selectedInstances.length <= 1) return true;
      let firstSetting = null;
      for (const instance of this.selectedInstances) {
        const settings = this.uniqueTemplateSettings[instance.instance_id];
        if (!settings) return false;
        const settingStr = `${settings.DPI}|${settings['File Type']}|${settings['Max File Size']}`;
        if (firstSetting === null) {
          firstSetting = settingStr;
        } else if (firstSetting !== settingStr) {
          return false;
        }
      }
      return true;
    },
    isXlsxPriceFormatSelected() {
      return ['xlsx_price'].includes(this.selectedFormat);
    },
    bulkNamesText() {
      return this.bulkNames.join('\n');
    },
    selectedFormatExtension() {
      const map = {
        jpg_from_psd: 'jpg',
        png_from_psd: 'png',
        psd_and_jpg: 'jpg',
      };
      return map[this.selectedFormat] ?? this.selectedFormat;
    },
    fileSizesTabDisabled() {
      // if (!this.compressionCanBeApplied) return true;
      // if (!this.compressToMaxFileSize) return true;
      // if (this.useTemplateSettings) return true;
      // if (!this.templateSettingsConsistent) return true;
      return false;
    }
  },
  watch : {
      async selectedFormat(newFormat) {
        this.selectedSize = SIZE_OPTIONS_TYPE.KEEP_ORIGINAL;
        this.size = {...initSize};

        await this.prepareModalData();
        this.updateGroupTemplatesItems();
        this.errors = [];
        this.resetLastResults();

        if (newFormat === 'pdf') {
          this.selectedDpi = 300;
          this.dpi = 300;
          this.selectedColorProfile = 'RGB';
        }
        
        if (newFormat !== 'pptx') {
          this.selectedNaming = NAMING.STANDARD;
        }

        this.selectedInstances = this.selectedInstances.filter(selectedInstance => {
          return this.instances.find(instance => instance.instance_id === selectedInstance.instance_id) !== undefined;
        });
      },
      async downloadInBackground(newValue) {
        await this.prepareModalData();
        this.updateGroupTemplatesItems();
        const visibleTemplates = this.layoutOptions.groupsTemplates.flatMap((group) => group.instancesId);

        this.selectedInstances = this.selectedInstances.filter(selectedInstance => {
          return this.instances.find(instance => instance.instance_id === selectedInstance.instance_id) !== undefined;
        });
        
        //if downloadInBackground update during settings change, not need add extra logic for selectedGroupInstances
        //because it already handled in applySavedSettings(), so we just skip
        if (this.isApplyingSavedSettings) {
          this.isApplyingSavedSettings = false; // important: reset
          return;
        }

        // Make it empty array when switch to non-background 
        // in order to avoid errors due to having non-presented on the page instances selected
        if (newValue === false) {
          const selectedVisibleGroupInstances = [];

          for (const groupData of this.groupInstancesMap) {
            const groupInstances = groupData.instances || [];
            const allGroupInstancesVisible = groupInstances.every(instance => visibleTemplates.includes(instance));

            if (!allGroupInstancesVisible) {
              continue;
            }

            selectedVisibleGroupInstances.push(...groupInstances);
          }

          this.selectedGroupInstances = selectedVisibleGroupInstances;
        }
      },
      selectedInstances(newSelectedInstances) {
          this.useTemplateSettings = false;
          //validate selected items
          this.validateSelected();
          const allSelected = newSelectedInstances.some(option => option.instance_id === -1);
          if (allSelected) {
              this.toggleSelectAllText();
          } else if (this.selectedInstances.length < this.instances.length) {
            this.selectAllText = 'Select All'
          } else if (this.selectedInstances.length === this.instances.length) {
            this.selectAllText = 'Unselect All'
          }

          if (!newSelectedInstances.length ||
            (this.selectedSize === SIZE_OPTIONS_TYPE.SCALE_DOWN_BY_DIMENSIONS &&
              (newSelectedInstances.length > 1 || newSelectedInstances.length === 0)
            )) {
            this.selectedSize = SIZE_OPTIONS_TYPE.KEEP_ORIGINAL;

            this.templateDimensions = {
              width: null,
              height: null,
            }
            
            this.size.dimensions = {
              width: null,
              height: null
            }
          } else {
            //if select all get first template
            let instanceId = newSelectedInstances[0].instance_id === -1 ? this.selectedInstances[0].instance_id : newSelectedInstances[0].instance_id;
            const preview = previews.find(preview => preview.id === instanceId);
            
            if (preview) {
              this.templateDimensions = {
                width: preview.template.width,
                height: preview.template.height
              };

              this.templateAspectRatio = preview.template.width / preview.template.height;
            }
            this.uniqueTemplateSettings = {};
            if (this.selectedInstances.length) {
              
              this.selectedInstances.map(instance => {
                let preview = previews.find(preview => preview.id === instance.instance_id);
                if (preview) {
                  if (preview.template && preview.template.fields) {
                    for (const field of preview.template.fields) {
                      if (['DPI', 'File Type', 'Max File Size'].includes(field.type)) {
                        let opt = JSON.parse(field.options);
                        if (!this.uniqueTemplateSettings[preview.id]) {
                          this.uniqueTemplateSettings[preview.id] = {
                            name: instance.name,
                            DPI: this.dpi,
                            'Max File Size': null,
                            'File Type': this.selectedFormat,
                          };
                        }

                        const fieldMapping = {
                          DPI: 'DPI',
                          'Max File Size': 'Max File Size',
                          'File Type': 'File Type',
                        };

                        if (fieldMapping[field.type]) {
                          this.uniqueTemplateSettings[preview.id][fieldMapping[field.type]] =
                            opt.Option1 || this.uniqueTemplateSettings[preview.id][fieldMapping[field.type]];
                        }
                      }
                    }
                  }
                }
              })
            }
          }

          if (this.isBulkNaming) {
            this.applyBulkName();
          }
      },
      selectedSize() {
        this.size.percentage = 100;
        
        if (this.selectedInstances.length === 1) {
          this.size.dimensions = {
            width: this.templateDimensions.width,
            height: this.templateDimensions.height
          }
        } else {
          this.size.dimensions = {
            width: null,
            height: null
          }
        }
    },
    dpi() {
      if (!DPI_PRESETS.some(({value}) => value === this.dpi)) {
        this.selectedDpi = 0
      }
    },
      instancesOption: {
        immediate: true,
        handler(newOptions) {
          if(this.selectedInstances.length === 0){
            if (newOptions.length === 2 && !this.selectedInstances.includes(newOptions[0])) {
              this.selectedInstances = [newOptions[0]];
            }
          }

          if (newOptions.find(option => option.$isDisabled)) {
            this.warningMessage = 'Some templates cannot be exported using this format because they are located on a different page'
          } else {
            this.warningMessage = null;
          }
        },
      }
  },
  mounted() {
    exportTemplatesEventBroker.on(EVENTS.INIT, this.init.bind(this));
    exportTemplatesEventBroker.on(EVENTS.SHOW, this.onShow.bind(this));
    exportTemplatesEventBroker.on(EVENTS.CLOSE, this.onClose.bind(this));
    exportTemplatesEventBroker.on(EVENTS.FINISH, this.onFinish.bind(this));

    const user_id = $('meta[name="userId"]').attr("content");
    let pusher = new Pusher(process.env.MIX_PUSHER_APP_KEY, {
      cluster: process.env.MIX_PUSHER_APP_CLUSTER
    });

    let channel = pusher.subscribe('export.'+user_id);
    channel.bind('fileAddToZip', this.onExportFileGenerated.bind(this));

    generateQuickDownloadEventBroker.on(
      GENERATE_QUICK_DOWNLOAD_EVENTS.GENERATED,
      this.onExportFileGenerated.bind(this),
      true
    );
  },
  methods: {
    //POR-8547 validate some additional fields to prevent errors if field was added with error
    validateSelected() {
      this.errors = [];
      if (!this.selectedInstances.length) {
        return;
      }
      const validationFields = [
        'left',
        'top',
        'width',
        'fontFamily'
      ];

      const validateNames = {
        left: 'X',
        top: 'Y',
        width: 'Width',
        fontFamily: 'Font'
      }
      
      this.selectedInstances.map(instance => {
        let preview = previews.find(preview => preview.id === instance.instance_id);
        if (preview) {
          if (['jpg_from_psd', 'png_from_psd', 'psd'].includes(this.selectedFormat)) {
            let additionalTextLayers = preview.canvas.getObjects().filter(obj => obj.visible === true && obj.element_id && String(obj.element_id).startsWith(ADDITIONAL_MULTI_LINE_TEXT_FIELD_PREFIX_ELEMENT_ID) );
            
            additionalTextLayers.forEach(layer => {
              //case for this font because this font was set as default but absent on the production. 
              // so need to check this case on production
              if (layer.fontFamily === 'AlternateGothicATF-Black' && location.host === 'app.rapidads.io') {
                this.errors.push(`Template ${instance.name} - ${layer.name} - please set Font before exporting.`);
              }

              validationFields.forEach(field => {
                if ((typeof layer[field] === "number" && isNaN(layer[field]))
                  || typeof layer[field] === "undefined") {
                  this.errors.push(`Template ${instance.name} - ${layer.name} - please set ${validateNames[field]} before exporting.`)
                }
              })
            })
          }
        }
      })
    },
    onPageNumerationUpdate(newPageNumeration) {
      this.pageNumeration = {
        ...newPageNumeration,
        underlyingShape: {
          ...newPageNumeration.underlyingShape,
          image: {...newPageNumeration.underlyingShape.image}
        }
      };
    },
    checkTestMode() {
      return this.layoutOptions?.test_mode === 'on';
    },
    copyToClipboard() {
      if (this.selectedFolder) {
        navigator.clipboard.writeText(window.location.origin + '/file/browser/files/' + this.selectedFolder.id)
          .then(() => {
            toastr.success('Folder link copied to clipboard!');
          })
          .catch(err => {
            console.error('Failed to copy: ', err);
          });
      } else {
        alert('No folder selected to copy.');
      }
    },
    async copySharedToClipboard() {
      if (!this.selectedFolder) {
        alert('No folder selected to share.');
        return;
      }

      this.isGeneratingShareLink = true;

      try {
        const { data } = await FileService.createFolderShareLink(this.selectedFolder.id);

        if (data.link) {
          await navigator.clipboard.writeText(data.link);

          toastr.success('Shared folder link copied to clipboard!');
        } else {
          toastr.error('Failed to generate share link');
        }
      } catch (error) {
        console.error('Failed to create share link:', error);
        toastr.error('Failed to generate share link');
      } finally {
        this.isGeneratingShareLink = false;
      }
    },
    openFolderInNewTab() {
      if (this.selectedFolder) {
        window.open(window.location.origin + '/file/browser/files/' + this.selectedFolder.id, '_blank');
      } else {
        alert('No folder selected to open.');
      }
    },
    validateJSON() {
      try {
        JSON.parse(this.jsonText);
        this.errorMessage = null;
      } catch (error) {
        this.errorMessage = "Invalid JSON: " + error.message;
      }
    },
    init({ layoutId, groupsTemplates, layout_options, isMasterAdmin, page, limit, tocLastPage }) {
      
      //sometimes after removed layout the groupTemplates is returned as Object of Objects instead of Array of Objects, so format this
      if (!Array.isArray(groupsTemplates) && typeof groupsTemplates === 'object') {
        groupsTemplates = Object.values(groupsTemplates);
      }
      //sometimes after removed layout the layout_options?.groupsTemplates is returned as Object of Objects instead of Array of Objects, so format this
      if (!Array.isArray(layout_options?.groupsTemplates) && typeof layout_options?.groupsTemplates === 'object') {
        layout_options.groupsTemplates = Object.values(layout_options.groupsTemplates)
      }
      
      this.layoutId = layoutId;
      this.groupsTemplates = groupsTemplates;
      this.layoutOptions = layout_options;
      this.isMasterAdmin = isMasterAdmin;
      this.compressToMaxFileSize = this.layoutOptions.compress_max_file_size || false;
      this.compressToMaxFileSizeValue = this.layoutOptions.compress_max_file_size_value || null;
      this.page = page;
      this.limit = limit;
      this.tocLastPage = tocLastPage;

      this.prepareModalData();
      this.initPdfOptions();
    },
    updateGroupTemplatesItems() {
      this.groupsInstances = [];

      const visibleTemplates = this.layoutOptions.groupsTemplates.flatMap((group) => group.instancesId);

      const firstGroupItems = this.groupsTemplates
          .map(group => group.instancesId?.[0])
          .filter(Boolean);

      const duplicatedFirstItems = new Set(
          firstGroupItems.filter((item, index) => firstGroupItems.indexOf(item) !== index)
      );

      this.groupsInstances = this.groupsTemplates.map((group, groupIndex) => {
        const groupItemsWithoutSharedFirst = group.instancesId.filter((instanceId, index) => {
          const isSharedElement = index === 0 && duplicatedFirstItems.has(instanceId);

          return !isSharedElement;
        });

        let isDisabled = this.disableUnrenderedTemaplates
            && !groupItemsWithoutSharedFirst.every(instanceId => visibleTemplates.includes(instanceId));

        if (this.selectedFormat === 'pdf' && this.downloadInBackground) {
          isDisabled = false;
        }

        const children = this.instances.filter(instance => group.instancesId.includes(instance.instance_id))
          .map(instance => {
            let childDisabled = this.disableUnrenderedTemaplates && isDisabled;

            if (this.selectedFormat === 'pdf' && this.downloadInBackground) {
              childDisabled = false;
            }

            return {
              text: instance.name,
              value: instance.instance_id,
              disabled: childDisabled,
              classes: "export-pdf-groups-dropdown__option",
            };
          });

        return {
          type: "optgroup",
          label: `Group ${groupIndex + 1}`,
          disabled: isDisabled,
          children: children,
        };
      });
    },
    async prepareModalData() {
      this.isTemplatesLoading = true;
      try {
        const getAllTemplates = this.selectedFormat === 'pdf' || (this.selectedFormat === 'pptx' && this.downloadInBackground) || this.reloadTemplates;
        if (getAllTemplates) {
          const page = getAllTemplates ? 1 : this.page;
          const limit = getAllTemplates ? 999 : this.limit;
          
          const {data: templates} = await layoutApi.getLayoutTemplates(this.layoutId, {
            simplified: true,
            sortBySettingsOrder: true,
            page,
            limit
          });
          
          // templates are loaded, we need to reload them after modal is closed
          this.reloadTemplates = false;
          this.instances = [...templates.map((template, index) => ({
            id: template.id,
            instance_id: template.instance_id,
            name: `${index + 1} - ${template.name}`
          }))];
          // Filter selected instances to ensure they are still in the new options
          if (this.selectedInstances && this.selectedInstances.length > 0) {
            this.selectedInstances = this.selectedInstances?.filter(selectedInstance => {
              return this.instances.some(option => option.instance_id === selectedInstance.instance_id);
            });
          }
          
          // fFilter also for group when type is GIF
          if (this.selectedGroupInstances && this.selectedGroupInstances.length > 0) {
            this.selectedGroupInstances = this.selectedGroupInstances?.filter(selectedGroupInstance => {
              return this.groupsTemplates.some(group => group.instancesId.includes(selectedGroupInstance));
            });
          }
          // add also filter group templates
          if (this.groupsTemplates && this.groupsTemplates.length > 0) {
            this.groupsTemplates = this.groupsTemplates.filter(group => {
              return group.instancesId.some(instanceId => {
                return this.instances.some(instance => instance.instance_id === instanceId);
              });
            });
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isTemplatesLoading = false;
      }

      if (this.groupsTemplates.length) {
        this.updateGroupTemplatesItems();

        this.groupInstancesMap = this.groupsTemplates.map((group, groupIndex) => ({
          group: groupIndex,
          instances: [...group.instancesId]
        }));
      }

      this.singleInstances = this.instances.filter(instance => {
        return !this.groupsTemplates.find((group) => {
          return group.instancesId.includes(instance.instance_id);
        })
      }).map(instance => {
        return {
          value: instance.instance_id,
          text: instance.name
        }
      });
    },
    async onShow() {
      this.$refs['export-templates-modal'].show();
      this.selectedDownloadSettings = null;
      this.activeDownloadSetting = null;
      this.reloadTemplates = true;
      await this.prepareModalData();
      this.loadFolders();
      this.loadSavedSettingsDropdown();
      this.$nextTick(() => this.$refs.savedDownloadSettingsTab.load());
      this.validateSelected()
      this.selectedInstances = this.instancesOption.filter(option => option.instance_id === this.selectedInstance?.id);
    },
    onClose() {
      this.isDownloadDropdownOpen = false;
      this.isSaving = false;
      this.showBar = false;
      this.selectedDownloadSettings = null;
      this.activeDownloadSetting = null;
      this.errors = [];
      this.resetLastResults();
      this.isDownloadingFileList = false;
      this.$refs['export-templates-modal'].hide();
    },
    onFinish(data = {}){
      this.lastResults = {
        saveToFolder: data.saveToFolder?.length > 0 ? data.saveToFolder : []
      }
      // re-initialize ruler after export
      rulerEventsBroker.fire(RULER_EVENTS.REINITIALIZE)
      this.isSaving = false;
      this.showBar = false;
    },
    validateSaveRequest() {
      if (this.selectedFormat === 'pdf' && (!this.selectedGroupInstances.length && !this.selectedSingleInstances.length)) {
        window.toastr.error("Please select at least one group or template from the list");
        return false;
      }

      if (this.selectedFormat === 'gif' && (!this.selectedGroupInstances.length && !this.selectedSingleInstances.length)) {
        window.toastr.error("Please select at least one group from the list");
        return false;
      }

      if (this.selectedFormat !== 'gif' && this.selectedFormat !== 'pdf' && !this.selectedInstances.length){
        window.toastr.error("Please select at least one template from the list");
        return false;
      }

      if (this.isNewFolder && !this.newFolderName) {
        window.toastr.error("Please provide a name for new folder");
        return false;
      }

      return true;
    },
    onSave() {
      this.resetLastResults();
      this.isSaving = true;

      if (! this.validateSaveRequest()) {
        this.isSaving = false;
        return;
      }

      if (this.selectedFormat !== 'gif' && this.selectedFormat !== 'pdf' && !this.downloadInBackground) {
        this.showBar = true;
        this.total = this.selectedInstances.length;
        this.current = 0;
      }

      const data = this.getDataToEvent();

      // update indexes for inserts
      if (data?.addPages?.insert?.isEnabled === true && data?.addPages?.insert?.insertions?.length) {
        data.addPages.insert.insertions.forEach((insertion) => {
          insertion.after.index = data.selectedGroupInstances.findIndex((selectedInstanceIndex) => {
            return this.groupInstancesMap[selectedInstanceIndex].instances.includes(insertion.after.instance_id);
          });
        });
      }
      exportTemplatesEventBroker.fire(EVENTS.EXPORT, {
        ...data,
        useTemplateSettings: this.useTemplateSettings,
      });

      if (this.downloadInBackground) {
        this.isSaving = false;
        this.showBar = false;
        this.$refs['export-templates-modal'].hide();
      }
    },
    getDataToEvent() {
      if (this.checkTestMode()) {
        return JSON.parse(this.jsonText);
      }

      if (this.pageNumeration) {
        this.pageNumeration['enableFromPage'] = this.tocLastPage;
      }

      const data = {
        allGroupInstancesMap: this.groupInstancesMap,
        exportFormat: this.selectedFormat,
        selectedNaming: this.selectedNaming,
        selectedInstances: this.selectedInstances,
        selectedGroupInstances: this.getSelectedTemplateGroups(),
        selectedSingleInstances: this.selectedSingleInstances,
        selectedHTMLPlatform: this.selectedPlatform,
        dpi: this.dpi,
        colorProfile: this.colorProfile,
        createPageForEachTemplate: this.createPageForEachTemplate,
        groupLayersNameByProjectName: this.groupLayersNameByProjectName,
        getDirectLinktoFile: this.getDirectLinktoFile,
        selectedFolder: this.selectedFolder,
        createOneFilePerGroup: this.createOneFilePerGroup,
        pdfPageSizeConfig: this.pdfPageSizeConfig,
        pptxPageSizeConfig: {
          type: this.selectedPptxPageSize
        },
        downloadInBackground: this.downloadInBackground,
        notifyEmail: this.notifyEmail,
        containsSyntheticPerformer: this.containsSyntheticPerformer,
        exportValidation: this.exportValidation,
        downloadAsOneFile: this.downloadAsOneFile,
        sizeOptions: {
          type: this.selectedSize,
          scalePercentage: this.size.percentage,
          dimensions: this.size.dimensions,
        },
        pptxIncludeFonts: this.pptxIncludeFonts,
        pageNumeration: this.pageNumeration,
        pageHeader: this.pageHeader,
        pageFooter: this.pageFooter,
        addPages: this.addPages,
        links: this.links,
        usePSD3: this.usePSD3,
        useMpdf: this.useMpdf,
        nativePdfTables: this.nativePdfTables,
        addBackgroundToImagesPdf: this.addBackgroundToImagesPdf,
        displayPrintableArea: this.displayPrintableArea,
        bleed: this.bleed,
        bleedLinked: this.bleedLinked,
        trimMarks: this.trimMarks,
        xlsxPrice: {},
        isNewFolder: this.isNewFolder,
        newFolderName: this.newFolderName,
        perTemplateMaxFileSizes: this.perTemplateMaxFileSizes,
        tableImageTransparentBackground: this.tableImageTransparentBackground,
        bulkNames: this.bulkNames,
        isBulkNaming: this.isBulkNaming
      };

      if (this.compressionCanBeApplied && this.compressToMaxFileSize) {
        data.compressionOptions = {
          compress: this.compressToMaxFileSize,
          compressValue: this.compressToMaxFileSizeValue,
        };
      }

      if (this.useTemplateSettings) {
        data.useTemplateSettings = this.useTemplateSettings;
      }

      if (this.isXlsxPriceFormatSelected) {
        data.xlsxPrice = {
          exportType: this.xlsxPrice.selectedExportType,
          tabNameSource: this.xlsxPrice.selectedTabNameSource,
          layoutId: this.layoutId,
        }
      } else {
        data.xlsxPrice = {};
      }

      return data;
    },
    selectAllOptions(selected) {
        if (selected) {
            this.selectedInstances = this.instances.map(instance => instance.instance_id);
        } else {
            this.selectedInstances = [];
        }
    },
    toggleSelectAllText() {
        if (this.selectAllText === 'Select All') {
            this.selectedInstances = this.instancesOption.filter(option => option.instance_id !== -1);
            this.selectAllText = 'Unselect All';
        } else {
            this.selectedInstances = [];
            this.selectAllText = 'Select All';
        }
    },
    selectAllInstances() {
      this.selectedInstances = [...this.instances];
      this.selectAllText = 'Unselect All';
      this.setSavedSettingsToNull();
    },
    unselectAllInstances() {
      this.selectedInstances = [];
      this.selectAllText = 'Select All';
      this.setSavedSettingsToNull();
    },
    isInstanceSelected(option) {
      return this.selectedInstances.some(instance => instance.instance_id === option.instance_id);
    },
    handleFolderSelection(folder) {
      this.selectedFolder = folder;
      this.isNewFolder = false;
      this.newFolderName = null;
      this.setSavedSettingsToNull();
    },
    handleNewFolderSelection(folderName) {
      this.selectedFolder = null;
      this.isNewFolder = true;
      this.newFolderName = folderName;
      this.setSavedSettingsToNull();
    },
    clearNewFolderSelection() {
      this.isNewFolder = false;
      this.newFolderName = null;
      this.setSavedSettingsToNull();
    },
    handleSelection(selectedFolder) {
      this.selectedFolder = selectedFolder.id;
    },
    handleWidthChange(newWidth) {
      this.size.dimensions.width = +newWidth;
      this.size.dimensions.height = Math.round(newWidth / this.templateAspectRatio);
      this.setSavedSettingsToNull();
    },
    handleHeightChange(newHeight) {
      this.size.dimensions.height = +newHeight;
      this.size.dimensions.width = Math.round(newHeight * this.templateAspectRatio);
      this.setSavedSettingsToNull();
    },
    getSelectedTemplateGroups() {
      const selectedGroups = new Set();
      
      this.selectedGroupInstances.forEach(instance => {
        const group = this.groupInstancesMap.find(groupInstance => groupInstance.instances.includes(instance));

        if (group) {
          selectedGroups.add(group.group);
        }
      });
      
      return Array.from(selectedGroups);
    },
    setJsonText() {
      let data = {
        exportFormat: this.selectedFormat,
        selectedInstances: this.selectedInstances,
        selectedGroupInstances: this.getSelectedTemplateGroups(),
        selectedSingleInstances: this.selectedSingleInstances,
        selectedHTMLPlatform: this.selectedPlatform,
        dpi: this.dpi,
        colorProfile: this.colorProfile, 
        createPageForEachTemplate: this.createPageForEachTemplate,
        groupLayersNameByProjectName: this.groupLayersNameByProjectName,
        getDirectLinktoFile: this.getDirectLinktoFile,
        selectedFolder: this.selectedFolder,
        createOneFilePerGroup: this.createOneFilePerGroup,
        downloadInBackground: this.downloadInBackground,
        notifyEmail: this.notifyEmail,
        downloadAsOneFile: this.downloadAsOneFile,
        sizeOptions: {
          type: this.selectedSize,
          scalePercentage: this.size.percentage,
          dimensions: this.size.dimensions,
        },
        pptxIncludeFonts: this.pptxIncludeFonts,
        pageNumeration: this.pageNumeration,
        pageHeader: this.pageHeader,
        pageFooter: this.pageFooter,
        links: this.links,
        usePSD3: this.usePSD3,
        useMpdf: this.useMpdf,
        nativePdfTables: this.nativePdfTables,
        tableImageTransparentBackground: this.tableImageTransparentBackground,
        bleed: this.bleed,
        bleedLinked: this.bleedLinked,
        trimMarks: this.trimMarks,
      };

      if (this.compressionCanBeApplied) {
        data.compressionOptions = {
          compress: this.compressToMaxFileSize,
          compressValue: this.compressToMaxFileSizeValue,
        };
      }

      this.jsonText = JSON.stringify(data, null, 2);
    },
    showSaveSettingsModal(event) {
      event.preventDefault();

      this.$refs.saveDownloadSettingsModal.show(this.getDataToEvent());
    },
    showUpdateSettingsModal(event) {
      event.preventDefault();

      if (!this.activeDownloadSetting) {
        return;
      }

      const currentSettingsName = this.selectedDownloadSettings?.text || this.activeDownloadSetting?.name;
      this.$refs.savedDownloadSettingsTab?.$refs.editDownloadSettingsModal.show(
        {
          ...this.activeDownloadSetting,
          name: currentSettingsName,
        },
        this.getDataToEvent()
      );
    },
    async loadSavedSettingsDropdown() {
      const { settings, downloadFormats } = await getSavedSettings();
      this.downloadSettingsOptions = settings.map(option => ({ text: option.name, value: option }));
      this.formats = downloadFormats;
      this.selectedFormat = this.formats?.find((format) => this.selectedFormat === format.value)?.value || this.selectedFormat;

      if (this.activeDownloadSetting?.id) {
        const updatedActiveSetting = settings.find((setting) => setting.id === this.activeDownloadSetting.id) || null;
        this.activeDownloadSetting = updatedActiveSetting;

        if (updatedActiveSetting) {
          this.selectedDownloadSettings = this.downloadSettingsOptions.find((option) => option.value.id === updatedActiveSetting.id) || null;
        } else {
          this.selectedDownloadSettings = null;
        }
      }
    },
    onSettingsUpdated() {
      this.loadSavedSettingsDropdown();
    },
    onSettingSaved() {
      this.loadSavedSettingsDropdown();
      this.$refs.savedDownloadSettingsTab.load();
    },
    syncBleed(value) {
      this.bleed.top = value;
      this.bleed.bottom = value;
      this.bleed.left = value;
      this.bleed.right = value;
    },
    handleBleedInput(side) {
      if (this.bleedLinked) {
        this.syncBleed(this.bleed[side]);
      }
      this.setSavedSettingsToNull();
    },
    onBleedLinkToggle() {
      if (this.bleedLinked) {
        // When linking is turned on, bring all sides in sync with the Top value.
        this.syncBleed(this.bleed.top);
      }
      this.setSavedSettingsToNull();
    },
    applyPdfOptions(selectedOptions) {
      this.pdfOptions.forEach((el) => {
        this[el.value] = selectedOptions.some(option => option.value === el.value);
      })
    },

    initPdfOptions() {
      this.selectedPdfOptions = [];
      for(const el of this.pdfOptions) {
        if (this[el.value]) {
          this.selectedPdfOptions.push(el);
        }
      }
      this.autoSelectSingleGroup();
    },
    autoSelectSingleGroup() {
      if(this.selectedFormat !== 'pdf' ) {
        this.selectedGroupInstances = [];
        return;
      }

      if (this.groupsInstances.length === 1) {
        const group = this.groupsInstances[0];

        // Extract all option values from the single group's children
        if (group.children && Array.isArray(group.children)) {
          this.selectedGroupInstances = group.children
            .filter(child => child.visible !== false)
            .map(child => child.value);
        }
      }
    },
    applySavedSettings(selectedOption) {
      this.activeDownloadSetting = selectedOption?.value || null;

      applySavedSettings(selectedOption.value, (settings) => {
        //reset selected instances and groups for correct work when we are selecting new settings and some instaces checked before
        this.selectedSingleInstances = [];
        this.selectedGroupInstances = [];
        this.selectedInstances = [];
        
        this.dpi = settings.dpi;
        this.selectedDpi = settings.dpi;
        this.colorProfile = settings.colorProfile || 'RGB';
        this.selectedColorProfile = this.colorProfile;
        this.selectedFormat = settings.exportFormat;
        this.selectedFolder = settings.selectedFolder;
        this.selectedPlatform = settings.selectedHTMLPlatform || '';
        this.createPageForEachTemplate = settings.createPageForEachTemplate;
        this.groupLayersNameByProjectName = settings.groupLayersNameByProjectName || false;
        this.createOneFilePerGroup = settings.createOneFilePerGroup;
        this.isApplyingSavedSettings = true;
        this.downloadInBackground = settings.downloadInBackground;
        this.notifyEmail = settings.notifyEmail || window.userEmail || '';
        this.downloadAsOneFile = settings.downloadAsOneFile;
        this.pptxIncludeFonts = settings.pptxIncludeFonts;
        this.getDirectLinktoFile = settings.getDirectLinktoFile;
        this.pageNumeration = settings.pageNumeration || {};
        this.usePSD3 = settings.usePSD3;
        this.useMpdf = settings.useMpdf;
        this.nativePdfTables = settings.nativePdfTables;
        this.selectedNaming = settings.selectedNaming || NAMING.STANDARD;
        this.addBackgroundToImagesPdf = settings.addBackgroundToImagesPdf || false;

        const _ptmfs = settings.perTemplateMaxFileSizes;
        this.perTemplateMaxFileSizes = (_ptmfs && !Array.isArray(_ptmfs) && typeof _ptmfs === 'object') ? _ptmfs : {};

        this.displayPrintableArea = settings.displayPrintableArea || false;
        this.tableImageTransparentBackground = settings.tableImageTransparentBackground || false;
        this.bleed = settings.bleed || { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' };
        this.bleedLinked = settings.bleedLinked ?? true;
        this.trimMarks = settings.trimMarks || false;

        if (settings?.isBulkNaming) {
          this.isBulkNaming = settings.isBulkNaming;
          this.applyBulkName();
        }

        const normalizePageEdgeSetting = (setting) => (
          setting && !Array.isArray(setting) && typeof setting === 'object'
            ? setting
            : {}
        );

        // For backwards compatability
        const pageFooterSetting = normalizePageEdgeSetting(settings.pageFooter);
        if (pageFooterSetting && pageFooterSetting.text && pageFooterSetting.text.isEnabled) {
          pageFooterSetting.isTextEnabled = true;
          pageFooterSetting.texts = [pageFooterSetting.text];
        }
        this.pageFooter = pageFooterSetting;

        const pageHeaderSetting = normalizePageEdgeSetting(settings.pageHeader);
        if (pageHeaderSetting && pageHeaderSetting.text && pageHeaderSetting.text.isEnabled) {
          pageHeaderSetting.isTextEnabled = true;
          pageHeaderSetting.texts = [pageHeaderSetting.text];
        }
        this.pageHeader = pageHeaderSetting;
        
        this.addPages = settings.addPages;
        this.links = Array.isArray(settings.links) ? settings.links : [];
        
        // Pdf page size
        const selectedPdfPageSizeType = settings.pdfPageSizeConfig?.type ?? 'none';
        const selectedPdfPageSizeOrientation = settings.pdfPageSizeConfig?.orientation;
        
        if (selectedPdfPageSizeType !== 'none' && selectedPdfPageSizeOrientation) {
          this.selectedPdfPageSize = selectedPdfPageSizeType + '-' + selectedPdfPageSizeOrientation;
        }

        this.selectedPptxPageSize = settings.pptxPageSizeConfig?.type ?? '';
        
        // add selected instances from settings if they present in layout and no instances were selected before
        if (
          settings.selectedInstances
          && settings.selectedInstances.length
          && !this.selectedInstances.length
        ) {
          settings.selectedInstances.forEach(settingSelectedInstance => {
            if (this.instances.find(instance => instance.instance_id === settingSelectedInstance.instance_id)) {
              this.selectedInstances.push(settingSelectedInstance);
            }
          });
        }

        // same as above
        if (
          settings.selectedSingleInstances
          && settings.selectedSingleInstances.length
          && !this.selectedSingleInstances.length
        ) {
          settings.selectedSingleInstances.forEach(settingSelectedInstance => {
            if (this.singleInstances.find(instance => instance.value === settingSelectedInstance)) {
              this.selectedSingleInstances.push(settingSelectedInstance);
            }
          });
        }

        // same as above
        if (
          settings.selectedGroupInstances
          && settings.selectedGroupInstances.length
          && !this.selectedGroupInstances.length
        ) {
          const visibleTemplates = this.layoutOptions.groupsTemplates.flatMap((group) => group.instancesId);

          settings.selectedGroupInstances.forEach(selectedGroupInstanceIndex => {
            let groupInstances = this.groupInstancesMap.find(instance => instance.group === selectedGroupInstanceIndex);

            if (groupInstances) {
              groupInstances = groupInstances.instances;

              for (const groupInstance of groupInstances) {
                if (!this.instances.find(instance => instance.instance_id === groupInstance)) {
                  continue;
                }

                const allGroupInstancesVisible = groupInstances.every(instance => visibleTemplates.includes(instance))
                if (this.disableUnrenderedTemaplates && !allGroupInstancesVisible) {
                  continue;
                }

                this.selectedGroupInstances.push(groupInstance);
              }
            }
          });

          this.selectedGroupInstances = [...this.selectedGroupInstances];
        }

        // selected size scale down by dimensions applyable only for single instance
        if (settings.sizeOptions.type === SIZE_OPTIONS_TYPE.SCALE_DOWN_BY_DIMENSIONS) {
          this.selectedSize = this.selectedInstances.length === 1
            ? settings.sizeOptions.type
            : SIZE_OPTIONS_TYPE.KEEP_ORIGINAL;
        } else {
          this.selectedSize = settings.sizeOptions.type;
        }

        this.$nextTick(() => {
          setTimeout(() => {
            if (this.selectedFolder) {
              this.$refs.selectedFolderInput.handleFolderChange(this.selectedFolder, true);
            }
            if (this.selectedSize === SIZE_OPTIONS_TYPE.SCALE_DOWN_BY_PERCENTAGE) {
              this.size.percentage = settings.sizeOptions.scalePercentage;
            } else if (this.selectedSize === SIZE_OPTIONS_TYPE.SCALE_DOWN_BY_DIMENSIONS) {
              this.size.dimensions.width = settings.sizeOptions.dimensions.width;
              this.size.dimensions.height = settings.sizeOptions.dimensions.height;
            }
          }, 0);
        });

        if (this.compressionCanBeApplied && settings.compressionOptions) {
          this.compressToMaxFileSize = settings.compressionOptions.compress ?? false;
          this.compressToMaxFileSizeValue = settings.compressionOptions.compressValue ?? null;
        }
      })
      this.initPdfOptions();
    },
    onExportTypeUpdated() {
      this.setSavedSettingsToNull()
      if (this.selectedFormat === 'pdf') {
        this.dpi = 300;
        this.selectedDpi = 300;
        this.selectedColorProfile = 'RGB';
        this.initPdfOptions();
      } else {
        this.autoSelectSingleGroup();
      }
    },
    setSavedSettingsToNull() {
      // Keep selected setting visible while user edits so it can be updated in-place.
    },
    updateDpi(selectedDpi) {
      if (selectedDpi === 0) {
        return
      }

      this.dpi = selectedDpi;
      this.setSavedSettingsToNull();
    },
    updateColorProfile(selectedColorProfile){
      this.colorProfile = selectedColorProfile;
      this.setSavedSettingsToNull();
    },
    async loadFolders() {
      try {
        const response = await folderService.getFolders();
        this.folders = response.data.items;
      } catch (error) {
        console.error('Failed to load folders:', error);
      }
    },
    handleCheckboxChange(changedField) {
      switch (changedField) {
        case 'compressToMaxFileSize':
          this.useTemplateSettings = false;
          break;
        case 'useTemplateSettings':
          this.useTemplateSettings = !!this.useTemplateSettings
          if (this.useTemplateSettings) {
            this.selectedInstances.map(instance => {
              let newName = instance.name;
              if (this.uniqueTemplateSettings && this.uniqueTemplateSettings[instance.instance_id]) {
                if (this.uniqueTemplateSettings[instance.instance_id]['DPI']) {
                  newName += ' (DPI: ' + this.uniqueTemplateSettings[instance.instance_id]['DPI'];
                }

                if (this.uniqueTemplateSettings[instance.instance_id]['Max File Size']) {
                  newName += ', Max file size: ' + this.uniqueTemplateSettings[instance.instance_id]['Max File Size'] + 'kb';
                }

                if (this.uniqueTemplateSettings[instance.instance_id]['File Type']) {
                  newName += ', File Type: ' + this.uniqueTemplateSettings[instance.instance_id]['File Type'];
                }
              }
              if (newName !== instance.name) {
                instance.orig_name = instance.name;
                instance.name = newName + ')';
              }
            })
          } else {
            this.selectedInstances.map(instance => {
              // ignore empty
              if (!instance.orig_name) return false;
              instance.name = instance.orig_name;
            })
          }
          break;
        default:
          this.compressToMaxFileSize = false;
          this.useTemplateSettings = false;
          break;
      }

      this.setSavedSettingsToNull();
    },
    resetLastResults() {
      this.lastResults = {
        saveToFolder: [],
      };
    },
    async downloadFileList() {
      this.isDownloadingFileList = true;

      if (this.lastResults.saveToFolder.length === 0) {
        return;
      }

      const data = this.lastResults.saveToFolder.map(result => {
        return {
          instanceId: result.templateIds[0],
          fileId: result.file.user_file_id
        }
      });

      const { data: blob } = await FileService.generateTemplatesFileLists(data)
      const layoutName = $('#template-group-preview').attr('data-layout-name').replace(/[ &/\\'"“”]/g, '_');
      downloadFileLink(URL.createObjectURL(blob), `File_list_${layoutName}.xlsx`);

      this.isDownloadingFileList = false;
    },
    onExportFileGenerated() {
      this.current++;
    },

    setBulkNaming() {
      if (this.isBulkNaming) {
        this.applyBulkName();
      } else {
        this.bulkNames = [];
      }
    },

    applyBulkName() {
      if (!this.bulkName) {
        this.bulkNames = [];

        return;
      }

      const ext = this.selectedFormatExtension;
      this.bulkNames = this.selectedInstances.map((_, i) => `${this.bulkName}_${i + 1}.${ext}`);
    },

    resetBulkNames() {
      this.applyBulkName();
    },

    onBulkNamesInput(event) {
      this.bulkNames = event.target.value.split('\n');
    }
  }
}
</script>
<style lang="scss">
@media (min-width: 576px) {
  #export-templates-modal .modal-dialog {
    width: 100% !important;
    max-width: 42rem !important;
    margin-right: auto;
    margin-left: auto;
  }
}
.dimensions-container {
  input {
    display: inline-block;
    width: 30%;
  }
}

.bleed-label {
  display: block;
  margin-bottom: 2px;
  font-size: 0.85rem;
}

.bleed-link-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 4px 0 0;
  font-size: 0.8rem;
  cursor: pointer;
  user-select: none;

  input {
    margin: 0;
  }

  i {
    color: #6c757d;
  }
}

.container-downloadable-templates .multiselect__tags {
    max-height: 135px;
    overflow: auto;
}

.export-pdf-groups-dropdown.multple-select-jquery .ms-choice,
#export-templates-modal .ms-drop li label,
#export-templates-modal .ms-drop li {
  white-space: normal !important;
  word-break: break-word !important;
  overflow-wrap: break-word !important;
  max-width: 100%;
}

#export-templates-modal .ms-drop {
  z-index: 1060;
  box-sizing: border-box;
  margin-top: 3px;
  border-color: #dfe5ed;
  border-radius: 4px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.14);

  ul > li {
    min-height: 32px;
    padding: 0;

    &:focus,
    &:hover {
      color: #fff;
      background: #19a17f;
    }
  }

  ul > li label {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 32px;
    padding: 7px 12px;
    line-height: 18px;
  }

  input[type="checkbox"] {
    flex: 0 0 16px;
  }
}

.export-pdf-groups-dropdown {
  &__option {
    pointer-events: none;
   
    label {
      display: -webkit-box !important;
      padding-left: 38px !important;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      max-height: 65px;
    }
    
    input {
      display: none;
    }
  }
}

textarea.json-editor {
  width: 100%; /* Заполнит всю доступную ширину */
  height: 300px; /* Высота редактора */
  font-family: monospace; /* Шрифт, удобный для кода */
  padding: 10px; /* Внутренние отступы */
  border: 1px solid #ccc; /* Граница редактора */
  border-radius: 5px; /* Скругленные углы */
  box-sizing: border-box; /* Учет отступов и границ в размере */
  resize: vertical; /* Пользователь может изменять только высоту */
}

#export-templates-modal .modal-body {
  padding: 0 !important;
  max-height: none;
  overflow: visible;

  .card {
    border: none !important;
    margin-bottom: 0 !important;

    .tabs {
      .card-header {
        background-color: transparent !important;
        padding: 0;
        border-bottom: 1px solid #e5eaf0;
        margin: 0 22px;
      }
    }
  }
}

#export-templates-modal {
  overflow-x: hidden;
  overflow-y: auto;

  .modal-content {
    overflow: visible;
    border: 1px solid #e2e7ed;
    border-radius: 8px;
    box-shadow: 0 18px 50px rgba(15, 23, 42, 0.14);
  }

  .modal-header {
    align-items: center;
    padding: 22px 22px 12px;
    border-bottom: 0;
  }

  .card-header-pills {
    margin: 0;
  }

  .nav-pills {
    align-items: stretch;
    gap: 16px;

    .nav-link {
      padding: 11px 16px;
      border-radius: 0;
      color: #64748b;
      background: transparent;
      font-size: 14px;
      font-weight: 600;

      &.active {
        color: #fff;
        background: #209c86;
      }
    }
  }

  .tab-content > .tab-pane {
    padding: 16px 22px 8px !important;
  }

  .export-download-tab {
    display: flex;
    flex-direction: column;

    > * {
      order: 5;
    }
  }

  .export-order-saved-settings {
    order: 1 !important;
  }

  .export-order-download {
    order: 2 !important;
  }

  .export-order-format {
    order: 3 !important;
  }

  .export-order-output {
    order: 4 !important;
  }

  .export-order-compression {
    order: 6 !important;
  }

  .export-order-folder {
    order: 7 !important;
  }

  .export-order-advanced {
    order: 8 !important;
  }

  .export-order-errors {
    order: 10 !important;
  }

  .export-field-row {
    display: grid;
    grid-template-columns: 140px minmax(0, 1fr);
    align-items: center;
    gap: 16px;
    margin: 0 0 19px !important;

    > [class*="col-md-"] {
      width: auto;
      max-width: none;
      padding: 0;
      flex: none;
    }

    > .form-group {
      margin-bottom: 0;
    }

    > label,
    > div:first-child > label,
    > div:first-child > label:first-child {
      margin: 0;
      color: #64748b;
      font-size: 14px;
      font-weight: 500;
    }
  }

  .export-output-row {
    align-items: start;
  }

  .export-field-label {
    padding-top: 5px;
  }

  .export-output-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 12px;

    &--single {
      grid-template-columns: minmax(0, 1fr);
      max-width: calc(50% - 6px);
    }
  }

  .export-output-field {
    min-width: 0;

    > label {
      display: block;
      margin: 0 0 6px;
      color: #64748b;
      font-size: 12px;
      font-weight: 600;
    }
  }

  .export-format-option {
    align-items: center;

    .form-control,
    .custom-select,
    .multiselect {
      width: 100%;
    }
  }

  .export-inline-checks {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px 28px;

    label {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      margin: 0;
      color: #172033;
      font-size: 14px;
      font-weight: 600;
    }
  }

  .export-bleed-option {
    align-items: start;

    > div:first-child {
      padding-top: 7px;
    }
  }

  .export-bleed-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr)) minmax(105px, 1.25fr);
    gap: 8px;
    margin: 0;

    > [class*="col-md-"] {
      width: auto;
      max-width: none;
      padding: 0;
      margin: 0;
      flex: none;
    }

    .form-control,
    .custom-select {
      width: 100%;
    }
  }

  .export-test-mode-option {
    align-items: start;

    textarea.json-editor {
      display: block;
      width: 100%;
      height: 220px;
      min-height: 140px;
      padding: 10px 12px;
      border-color: #dfe5ed;
      border-radius: 4px;
      color: #172033;
      background: #fff;
      font-size: 13px;
      box-shadow: none;

      &:focus {
        border-color: #209c86;
        outline: 0;
        box-shadow: 0 0 0 2px rgba(32, 156, 134, 0.12);
      }
    }
  }

  .export-dpi-controls {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
  }

  .export-download-tab {
    .export-select,
    .multiselect,
    .multple-select-jquery,
    .folder-tree-select {
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }

    .multple-select-jquery.form-control {
      height: auto;
      min-height: 40px;
      padding: 0;
      border: 0;
      background: transparent;
    }

    .multple-select-jquery .ms-choice {
      position: relative;
      height: 40px;
      padding: 0;
      border: 1px solid #dfe5ed;
      border-radius: 4px;
      background: #fff;
      line-height: 38px;

      > span {
        display: block;
        position: absolute !important;
        top: 50% !important;
        bottom: auto;
        left: 0 !important;
        right: 38px;
        padding: 0 0 0 12px;
        line-height: 20px;
        transform: translateY(-50%);
        pointer-events: none;
      }

      > div.icon-caret {
        right: 12px;
        pointer-events: none;
      }
    }

    .multple-select-jquery.ms-parent-open .ms-choice,
    .multple-select-jquery .ms-choice:focus {
      border-color: #209c86;
      box-shadow: 0 0 0 2px rgba(32, 156, 134, 0.12);
    }

    .multiselect:not(.export-select) {
      .multiselect__content-wrapper {
        margin-top: 3px;
        border-color: #dfe5ed;
        border-radius: 4px;
        box-shadow: 0 6px 18px rgba(15, 23, 42, 0.14);
      }

      .multiselect__option {
        min-height: 32px;
        padding: 7px 12px;
        line-height: 18px;

        &--selected {
          color: #172033;
          background: #f4f7fa;
          font-weight: 400;
        }

        &--highlight,
        &--selected.multiselect__option--highlight {
          color: #fff;
          background: #19a17f;
        }
      }
    }

    .multiselect--active .multiselect__tags,
    .multiselect:focus-within .multiselect__tags {
      border-color: #209c86;
      box-shadow: 0 0 0 2px rgba(32, 156, 134, 0.12);
    }

    .export-settings-dropdown .multiselect__option::after {
      display: flex;
      align-items: center;
      top: 0;
      bottom: 0;
      height: auto;
      padding: 0 10px;
      font-size: 12px;
      line-height: 18px;
    }

  }

  .folder-tree-select__dropdown {
    box-sizing: border-box;
    border-color: #dfe5ed;
    border-radius: 4px;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.14);

    .tree-select-item:hover {
      color: #fff;
      background: #19a17f;

      svg {
        color: inherit;
      }
    }
  }

  .form-control,
  .custom-select,
  .multiselect__tags,
  .ms-choice,
  .folder-tree-select input {
    min-height: 40px;
    border-color: #dfe5ed;
    border-radius: 4px;
    color: #172033;
    background-color: #fff;
    font-size: 14px;
    box-shadow: none;
  }

  .form-control:focus,
  .custom-select:focus,
  .folder-tree-select input:focus {
    border-color: #209c86;
    box-shadow: 0 0 0 2px rgba(32, 156, 134, 0.12);
  }

  .multiselect {
    min-height: 40px;
  }

  .multiselect__tags {
    padding: 9px 38px 7px 12px;
  }

  .multiselect__select {
    height: 38px;
  }

  .multiselect__placeholder,
  .multiselect__single,
  .export-selection-summary {
    display: inline-block;
    margin: 0;
    padding: 0;
    color: #172033;
    font-size: 14px;
    line-height: 20px;
  }

  .multiselect__placeholder {
    color: #172033;
  }

  .export-download-tab {
    .form-control::placeholder,
    .folder-tree-select input::placeholder {
      color: #172033;
      opacity: 1;
    }

    .multiselect__placeholder,
    .multiselect__single,
    .multiselect__tag,
    .export-selection-summary,
    .ms-choice > span,
    .folder-tree-select__trigger-label {
      color: #172033;
    }
  }

  .container-downloadable-templates {
    .multiselect--active {
      z-index: 1070;
    }

    .multiselect__content-wrapper {
      overflow-x: hidden;
      border-color: #dfe5ed;
      box-shadow: 0 5px 14px rgba(15, 23, 42, 0.12);
    }

    .multiselect__content,
    .multiselect__element {
      display: block !important;
      width: 100%;
      min-width: 0;
    }

    .multiselect__option {
      box-sizing: border-box;
      width: 100%;
      min-width: 0;
      min-height: 38px;
      padding: 8px 16px;
      color: #172033;
      background: #fff;

      &::after {
        display: none;
      }

      &--highlight,
      &--selected,
      &--selected.multiselect__option--highlight {
        color: #fff;
        background: #19a17f;
      }
    }
  }

  .export-template-actions {
    display: flex;
    align-items: center;
    min-height: 34px;
    padding: 5px 16px;
    border-bottom: 1px solid #e5eaf0;

    button {
      padding: 2px 0;
      border: 0;
      color: #209c86;
      background: transparent;
      font-size: 12px;

      &:hover,
      &:focus {
        text-decoration: underline;
      }
    }

    &__unselect {
      margin-left: auto;
      color: #64748b !important;
    }
  }

  .export-template-option {
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    min-width: 0;

    > span:last-child {
      display: block;
      flex: 1 1 0;
      width: 0;
      max-width: 100%;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      line-height: 20px;
    }

    &__checkbox {
      position: relative;
      flex: 0 0 16px;
      width: 16px;
      height: 16px;
      border: 1px solid #4ba183;
      border-radius: 2px;
      background: #fff;

      &--checked {
        background: #4ba183;

        &::after {
          content: '';
          position: absolute;
          top: 1px;
          left: 5px;
          width: 5px;
          height: 9px;
          border: solid #fff;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }
      }
    }
  }

  .export-checkbox-setting {
    display: flex;
    align-items: center;
    gap: 12px;

    label {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      margin: 0;
      color: #172033;
      font-size: 14px;
      font-weight: 600;
    }
  }

  .export-checkbox-setting__label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    color: #172033;
    font-size: 14px;
    font-weight: 600;
  }

  .export-synthetic-performer-option {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    color: #64748b;
    white-space: nowrap;
  }

  .export-synthetic-performer-option__content {
    display: inline-flex;
    flex-direction: column;
  }

  .export-synthetic-performer-option__description {
    font-size: 10px;
  }

  input[type="checkbox"] {
    position: relative;
    width: 16px;
    height: 16px;
    margin: 0;
    appearance: none;
    border: 1px solid #209c86;
    border-radius: 2px;
    background: #fff;
    vertical-align: middle;

    &:checked {
      border-color: #209c86;
      background-color: #209c86;

      &::after {
        content: '';
        position: absolute;
        top: 1px;
        left: 5px;
        width: 5px;
        height: 9px;
        border: solid #fff;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
      }
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }
  }

  .export-max-file-size {
    display: flex;
    align-items: center;
    gap: 8px;

    .form-control {
      width: 110px;
      height: 34px;
      min-height: 34px;
    }

    span {
      color: #64748b;
      font-size: 13px;
    }
  }

  .export-advanced {
    margin-bottom: 20px;
    padding: 4px 8px 0 0;
    border-radius: 4px;
  }

  .export-advanced-toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
    border: 0;
    color: #64748b;
    background: transparent;
    font-size: 14px;

    i {
      font-size: 12px;
      transition: transform 0.2s ease;
    }

    &[aria-expanded="true"] i {
      transform: rotate(180deg);
    }
  }

  .export-advanced-content {
    padding: 0 0 4px;

    .export-field-row {
      margin-bottom: 12px !important;
    }

    label {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 0;
    }
  }

  .export-advanced-bulk-naming {
    align-items: start;
    margin-left: 156px !important;
    grid-template-columns: 140px minmax(0, 1fr);

    > div:first-child {
      padding-top: 10px;
    }

    .form-control:disabled {
      color: #94a3b8;
      background: #f8fafc;
      opacity: 1;
    }
  }

  .export-bulk-names-editor {
    margin-top: 8px;
    padding: 10px;
    border: 1px solid #dfe5ed;
    border-radius: 4px;
    background: #f8fafc;

    &__header,
    &__footer {
      color: #64748b;
      font-size: 12px;
      font-weight: 600;
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 7px;

      button {
        padding: 0;
        border: 0;
        color: #64748b;
        background: transparent;
        font-size: inherit;
        text-decoration: underline;
      }
    }

    textarea.form-control {
      min-height: 108px;
      padding: 8px 12px;
      font-family: monospace;
      line-height: 1.35;
      resize: vertical;
    }

    &__footer {
      margin-top: 7px;
    }
  }

  .modal-footer {
    padding: 0px 16px 14px 16px;
    border-top: 0;

    .export-save-settings-link {
      display: inline-flex;
      align-items: center;
      min-height: 40px;
      color: #0b987f;
      font-size: 14px;
    }

    .btn {
      min-height: 40px;
      padding: 8px 16px;
      border-radius: 4px;
      font-size: 14px;
      font-weight: 600;
    }

    .btn-primary {
      border-color: #209c86;
      background: #209c86;
    }

    .btn-secondary {
      border-color: #209c86;
      color: #fff;
      background: #209c86;
    }
  }
}

@media (max-width: 767.98px) {
  #export-templates-modal {
    .export-field-row {
      grid-template-columns: minmax(0, 1fr);
      gap: 7px;
    }

    .export-output-grid,
    .export-output-grid--single {
      grid-template-columns: minmax(0, 1fr);
      max-width: none;
    }

    .export-advanced-bulk-naming {
      margin-left: 0 !important;
      grid-template-columns: minmax(0, 1fr);
    }

    .export-bleed-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));

      > div:last-child {
        grid-column: 1 / -1;
      }
    }

    .nav-pills {
      gap: 4px;
    }
  }
}

.my-tooltip-inner {
  max-width: 300px !important;
  white-space: normal;
}

#export-templates-modal {
  label {
    cursor: pointer;
    
    input[type="checkbox"] {
      cursor: pointer;
    }
  }
}
.tracking-tight {
  letter-spacing: -.025em;
}
.leading-none {
  line-height: 1 !important;
}
.font-semibold {
  font-weight: 600;
}
.text-lg {
  font-size: 1.125rem;
  line-height: 1.75rem;
}
.template-settings-label {
  color: #64748b !important;
}
</style>
